// Human-like publishing schedule generator.
//
// This runs ONLY at "Generate Schedule" time (a one-time calculation).
// The resulting timestamps are saved to the database as-is; the publishing
// cron/edge functions must only read `scheduled_at` and never call this again.

const PLATFORM_JITTER_MINUTES = {
  facebook: 30,
  pinterest: 15,
  threads: 30,
};

// Facebook's native scheduler rejects scheduled_publish_time values under 10
// minutes from now. Slots are clamped to this minimum lead time (measured
// from actual generation time, not just the base+delay math) so jitter can
// never erode the delay below Facebook's hard floor, with headroom for the
// time a user spends reviewing the queue before hitting "Schedule Posts".
const PLATFORM_MIN_LEAD_MINUTES = {
  facebook: 15,
};

// Safety buffer so a jittered slot can never cross into a neighboring slot,
// which would reorder posts or collapse the gap between them to ~0.
const MIN_GAP_BUFFER_MINUTES = 3;

// Minutes considered "robotic" clean times (on the hour / half hour).
const isRoundClockTime = (date) => {
  const m = date.getMinutes();
  return m === 0 || m === 30;
};

const clockKey = (date) =>
  `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

const minuteKey = (date) => Math.floor(date.getTime() / 60000);

const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

/**
 * Generates `count` non-robotic publishing timestamps spaced around a base
 * cadence (`intervalMinutes`), starting `initialDelayMinutes` after `baseDate`.
 *
 * Final time = slot time (base + delay + i*interval) + random jitter, where the
 * jitter is bounded per platform and clamped so slots never collide, never land
 * on a duplicate minute, never repeat the same clock time (HH:mm) as another
 * generated slot, and avoid exact :00 / :30 marks when possible.
 *
 * Average spacing between posts still equals `intervalMinutes`, so posting
 * frequency is unchanged — only the individual gaps and clock times vary.
 */
export function generateHumanSchedule({
  platform,
  count,
  baseDate = new Date(),
  intervalMinutes,
  initialDelayMinutes = 0,
}) {
  if (!count || count <= 0) return [];

  const platformJitter = PLATFORM_JITTER_MINUTES[platform] ?? 20;
  const safeInterval = Math.max(1, Number(intervalMinutes) || 1);
  const maxJitter = Math.max(
    1,
    Math.min(platformJitter, Math.floor(safeInterval / 2) - MIN_GAP_BUFFER_MINUTES) || 1
  );

  const base = new Date(baseDate);
  base.setSeconds(0, 0);

  const usedMinutes = new Set();
  const usedClockTimes = new Set();
  const results = [];

  for (let i = 0; i < count; i++) {
    const slotCenter = new Date(base.getTime() + (initialDelayMinutes + i * safeInterval) * 60000);

    let finalTime = null;
    for (let attempt = 0; attempt < 25; attempt++) {
      const offset = randomInt(-maxJitter, maxJitter);
      const candidate = new Date(slotCenter.getTime() + offset * 60000);

      if (isRoundClockTime(candidate)) continue;
      if (usedMinutes.has(minuteKey(candidate))) continue;
      if (usedClockTimes.has(clockKey(candidate))) continue;

      finalTime = candidate;
      break;
    }

    if (!finalTime) {
      // Fallback: nudge minute-by-minute off the slot center until unique/non-round.
      let nudge = 1;
      let candidate = new Date(slotCenter.getTime());
      while (usedMinutes.has(minuteKey(candidate)) || isRoundClockTime(candidate)) {
        candidate = new Date(slotCenter.getTime() + nudge * 60000);
        nudge += 1;
      }
      finalTime = candidate;
    }

    const minLeadMinutes = PLATFORM_MIN_LEAD_MINUTES[platform];
    if (minLeadMinutes) {
      const earliestAllowed = new Date(Date.now() + minLeadMinutes * 60000);
      if (finalTime < earliestAllowed) finalTime = earliestAllowed;
    }

    usedMinutes.add(minuteKey(finalTime));
    usedClockTimes.add(clockKey(finalTime));
    results.push(finalTime);
  }

  return results;
}

// Formats a Date as a `<input type="datetime-local">` value (local time, minute precision).
export function toDatetimeLocalValue(date) {
  const pad = (v) => String(v).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}
